import { ReactElement, useEffect, useState } from 'react';
import { Field, useFormikContext } from 'formik';
import { navToError } from '@utils';
import { useLocation, useNavigate } from 'react-router-dom';
import { FormType } from '@constants/forms';
import { iObligation } from '@interfaces/interfaces';
import { iReviewFormValues } from '@services/ReviewService';
import { getObligationOptions } from '@api';
import { FormLoading } from '@components/index';
import ExpandableTableAccessible from '@components/Review/ExpandableTableAccessible/ExpandableTableAccessible';

import styles from './ReviewStep11.module.scss';

function ReviewStep11(): ReactElement {
  const [loading, setLoading] = useState(true);
  const context = useFormikContext();
  const { values }: { values: iReviewFormValues } = useFormikContext();

  const location = useLocation();
  const navigate = useNavigate();

  const [authorisedOb, setObligationDetails] = useState({} as iObligation);
  const [eligibleObs, setEligibleObligations] = useState([] as iObligation[]);

  const summaryColumns = [
    {
      key: 'number',
      label: 'Obligation #',
      value: authorisedOb.Number,
      width: '1.04fr',
      valueClassName: styles.obligationNumber,
    },
    {
      key: 'offenceDate',
      label: 'Offence Date',
      value: authorisedOb.OffenceDate,
      width: '1.03fr',
    },
    {
      key: 'offenceDesc',
      label: 'Offence Description',
      value: authorisedOb.OffenceDesc,
      width: '2.09fr',
    },
    {
      key: 'amountDue',
      label: 'Amount',
      value: authorisedOb.AmountDue ? `$${authorisedOb.AmountDue}` : '',
      width: '1fr',
    },
  ].filter((column) => Boolean(column.value));

  const summaryGridTemplateColumns = summaryColumns.length > 0
    ? summaryColumns.map((column) => `minmax(0, ${column.width})`).join(' ')
    : '1fr';

  useEffect(() => {
    try {
      setLoading(true);
      const obNumFormIk = values.OBLIGATION_NUMBER;
      if (values.OBLIGATION_NUMBER) {
      getObligationOptions(obNumFormIk, FormType.REVIEW)
        .then((responseBody) => {
          if (responseBody && responseBody?.Authorised) {
            setObligationDetails(responseBody?.Authorised);
            context.setFieldValue('OBLIGATION_NUMBER', responseBody?.Authorised.Number);
            const [day, month, year] = responseBody.Authorised.OffenceDate.split('/').map(Number);
            context.setFieldValue('OFFENCE_DATE', new Date(year, month - 1, day));
            context.setFieldValue('AuthorisedObligation', responseBody?.Authorised);
            context.setFieldValue('InfringementNumber', responseBody?.Authorised.InfringementNumber);
          }
          if (responseBody && responseBody?.Obligations) {
            setEligibleObligations(responseBody?.Obligations);
            context.setFieldValue('EligibleObligations', responseBody?.Obligations);
            context.setFieldValue('PersonUnawareIneligibleList', responseBody?.PersonUnawareIneligibleList);
            context.setFieldValue('NumberOfValidEnforcementReview', responseBody?.NumberOfValidEnforcementReview);
            context.setFieldValue('NumberOfValidInternalReview', responseBody?.NumberOfValidInternalReview);
            context.setFieldValue('NumberOfInvalidSevenDayNotice', responseBody?.NumberOfInvalidSevenDayNotice);
          }
          if (responseBody?.PersonUnawareIneligibleList.length > 0 && values.isEnforcementReviewStage && values.selectTheOption.includes('SelectedGroundInfo3')) {
            const PUIneligbleNums = responseBody?.PersonUnawareIneligibleList.map((ob) => ob.Number);
            const filteredEligibleObligations = responseBody?.Obligations.filter((ob) => !PUIneligbleNums.includes(ob.Number));
            if (values.SelectedObligationsStep11.length > 0) {
              const filteredInvalidObs = values.SelectedObligationsStep11.filter((ob) => PUIneligbleNums.includes(ob.number));
              if (filteredInvalidObs.length > 0) context.setFieldValue('SelectedObligationsStep11', []);
            }
            setEligibleObligations(filteredEligibleObligations);
            context.setFieldValue('EligibleObligations', filteredEligibleObligations);
          }
          // Handling scenario where a saved form is started using a different obligation number
          if (values.SelectedObligationsStep11.length > 0) {
            const eligibleNum = responseBody?.Obligations.some((ob) => ob.Number === values.SelectedObligationsStep11[0].number);
            if (!eligibleNum) {
              context.setFieldValue('SelectedObligationsStep11', []);
            }
          }
          setLoading(false);
        })
        .catch(() => navToError(navigate, location));
      } else {
        navToError(navigate, location);
      }
    } catch (ex) {
      navToError(navigate, location);
    }
    return () => {
      setLoading(false);
    };
  }, [values.OBLIGATION_NUMBER]);

  return (
    <>
      {loading && <FormLoading />}
      {!loading && (
        <>
          <div className="vfH2">Your fines</div>
          <p>You are requesting a review of the following fine:</p>
          <div className={styles.offenceDetails}>
            {summaryColumns.length > 0 && (
              <>
                <div className={styles.detailHeadings} style={{ gridTemplateColumns: summaryGridTemplateColumns }}>
                  {summaryColumns.map((column) => (
                    <span key={column.key}>{column.label}</span>
                  ))}
                </div>
                <div className={styles.detailValues} style={{ gridTemplateColumns: summaryGridTemplateColumns }}>
                  {summaryColumns.map((column) => (
                    <span key={column.key} className={column.valueClassName || ''}>{column.value}</span>
                  ))}
                </div>
              </>
            )}
            <div className={styles.detailTable}>
              <table className={styles.offenceTable}>
                <tbody>
                  {
                  authorisedOb.Number && (
                  <tr className={styles.onlyMobileRows}>
                    <td className={styles.tdFirstColBackground}>Obligation #</td>
                    <td className={styles.tdSecondColBackground}>{authorisedOb.Number}</td>
                  </tr>
                  )
                  }
                  {
                  authorisedOb.OffenceDate && (
                  <tr className={styles.onlyMobileRows}>
                    <td className={styles.tdFirstColBackground}>Offence Date</td>
                    <td className={styles.tdSecondColBackground}>{authorisedOb.OffenceDate}</td>
                  </tr>
                  )
                  }
                  {
                  authorisedOb.OffenceDesc && (
                  <tr className={styles.onlyMobileRows}>
                    <td className={styles.tdFirstColBackground}>Offence Description</td>
                    <td className={styles.tdSecondColBackground}>{authorisedOb.OffenceDesc}</td>
                  </tr>
                  )
                  }
                  {
                  authorisedOb.AmountDue && (
                  <tr className={styles.onlyMobileRows}>
                    <td className={styles.tdFirstColBackground}>Amount</td>
                    <td className={styles.tdSecondColBackground}>${authorisedOb.AmountDue}</td>
                  </tr>
                  )
                  }
                  {
                  authorisedOb.OffenceLocation && (
                  <tr className={styles.hideInMobileView}>
                    <td>Offence Location</td>
                    <td className={styles.uppcaseText}>{authorisedOb.OffenceLocation}</td>
                  </tr>
                  )
                  }
                  {
                  authorisedOb.OffenceTime && (
                  <tr className={styles.hideInMobileView}>
                    <td>Time of Offence</td>
                    <td>{authorisedOb.OffenceTime}</td>
                  </tr>
                  )
                  }
                  {
                  authorisedOb.Registration && (
                  <tr className={styles.hideInMobileView}>
                    <td>Vehicle Registration</td>
                    <td>{authorisedOb.Registration}</td>
                  </tr>
                  )
                  }
                  {
                  authorisedOb.OffenceLocation && (
                  <tr className={styles.onlyMobileRows}>
                    <td colSpan={2}>
                      Offence Location <br /> <span className={styles.tdSpanFont}>{authorisedOb.OffenceLocation}</span>
                    </td>
                  </tr>
                  )
                  }
                  {
                  authorisedOb.OffenceTime && (
                  <tr className={styles.onlyMobileRows}>
                    <td colSpan={2}>
                      Time of Offence <br /> <span className={styles.tdSpanFont}>{authorisedOb.OffenceTime}</span>
                    </td>
                  </tr>
                  )
                  }
                  {
                  authorisedOb.Registration && (
                  <tr className={styles.onlyMobileRows}>
                    <td colSpan={2}>
                      Vehicle Registration <br /> <span className={styles.tdSpanFont}>{authorisedOb.Registration}</span>
                    </td>
                  </tr>
                  )
                  }
                </tbody>
              </table>
            </div>
          </div>
          {values.isInternalReviewStage && (
            <>
              {eligibleObs.length !== 0 && (
                <>
                  <p>
                    <strong>
                      Which other eligible fines do you want to include in the application?
                    </strong>
                  </p>
                  <Field
                    name="SelectedObligationsStep11"
                    obligations={eligibleObs}
                    inactiveRows={false}
                    component={ExpandableTableAccessible}
                    selectedRows={false}
                  />
                  {/* <ExpanadableTableAccessible obligations={eligibleObs} inactiveRows={false} selectedRows={false} /> */}
                </>
              )}
              <br />
              {values.NumberOfValidEnforcementReview > 0 && (
                <>
                  <p>
                    You have
                    {' '}
                    <strong>{values.NumberOfValidEnforcementReview}</strong>
                    {' '}
                    fine(s) not included in the list above. They are at Notice of Final Demand and/or Enforcement Warrant stage, so they are at a different stage to the fine you are dealing with now.
                  </p>
                  <p>If you want to request a review for these fines, you will need to log in again using one of the fines and complete a separate review application for them.</p>
                </>
              )}
            </>
          )}
          {values.isEnforcementReviewStage && (
            <>
              <p>
                Because a Notice of Final Demand has been issued in relation to your fine, Fines Victoria will process your application as an enforcement review.
              </p>
              <p>
                If Fines Victoria cancels enforcement as a result of your application, the issuing agency must withdraw the infringement notice and either take no further action, issue an official warning or refer the matter to court. If applying under Person Unaware, the issuing agency may also cancel and reissue the fine. You will be notified in writing if this occurs.
              </p>
              <p>
                By law, you can only make one application for an enforcement review. If you apply under the ground of Special Circumstances, you can apply twice, even if you have applied before under a different review ground.
              </p>
              {eligibleObs.length !== 0 && (
                <>
                  <p>
                    <strong>
                      Which other eligible fines do you want to include in the application?
                    </strong>
                  </p>
                  <Field
                    name="SelectedObligationsStep11"
                    obligations={eligibleObs}
                    inactiveRows={false}
                    component={ExpandableTableAccessible}
                    selectedRows={false}
                  />
                  {/* <ExpanadableTableAccessible obligations={eligibleObs} inactiveRows={false} selectedRows={false} /> */}
                  {/* Fines at Review Stage */}
                </>
              )}
              {values.NumberOfValidInternalReview > 0 && (
                <>
                  <br />
                  <p>
                    You have
                    {' '}
                    <strong>{values.NumberOfValidInternalReview}</strong>
                    {' '}
                    fine(s) not included in the list above. They are at Infringement stage, so they are at a different stage to the fine you are dealing with now.
                  </p>
                  <p>If you want to request a review for these fines, you will need to log in again using one of the fines and complete a separate review application for them.</p>
                </>
              )}
              {/* If Person Unaware was selected and some of the fines belong to PU offence code */}
              {values.selectTheOption.includes('SelectedGroundInfo3') && values.PersonUnawareIneligibleList.length > 0 && (
                <>
                  <br />
                  <p>
                    You have
                    {' '}
                    <strong>{values.PersonUnawareIneligibleList.length}</strong>
                    {' '}
                    fine(s) that are not eligible for Enforcement Review under the grounds of Person Unaware.
                  </p>
                  <p>
                    If you only became aware of these fines in the last 14 days, you may be able to apply for an
                    {' '}
                    <a href="/Your-options/Infringement-extension" target="_blank" rel="noopener noreferrer">
                      Infringement Extension
                    </a>
                    {' '}
                    to get more time to deal with them.
                  </p>
                </>
              )}
              {/* If Seven Day Notice has been served  */}
              {values.NumberOfInvalidSevenDayNotice > 0 && (
                <>
                  <br />
                  <p>
                    You have
                    {' '}
                    <strong>{values.NumberOfInvalidSevenDayNotice}</strong>
                    {' '}
                    fine(s) that are not eligible for Enforcement Review because a 7 Day Notice has been served and expired. Please contact Fines Victoria on
                    {' '}
                    <a href="tel:0392008222" title="Fines Victoria phone number">
                      03 9200 8222
                    </a>
                    {' '}
                    or
                    {' '}
                    <a href="tel:1800150410" title="Fines Victoria phone number">
                      1800 150 410
                    </a>
                    {' '}
                    (regional callers).
                  </p>
                </>
              )}
            </>
          )}
          <br />
          <p className={styles.middleNoteSection}>This summary may not be a complete listing of your fines. Infringement information may vary as a result of a difference in:</p>
          <div>
            <ul>
              <li>the spelling of your name</li>
              <li>your address</li>
              <li>your driver licence number</li>
            </ul>
          </div>
        </>
      )}
    </>
  );
}

export default ReviewStep11;
